<!-- PDF preview overlay -->
<div class="preview-overlay" *ngIf="showPreview" role="dialog" aria-modal="true" aria-label="Transfer Out Preview">
  <div class="preview-container">
    <div class="preview-header">
      <h3>Transfer Out Preview</h3>
      <div class="d-flex gap-2">
        <button class="btn btn-secondary" (click)="printPdf()">
          <i class="bi bi-printer"></i> Print
        </button>
        <button class="btn btn-secondary" (click)="downloadPdf()">
          <i class="bi bi-download"></i> Download
        </button>
        <button class="btn btnprimary" (click)="closePreview()">
          <i class="bi bi-check2"></i> Done
        </button>
      </div>
    </div>
    <div class="preview-body">
      <div *ngIf="isLoadingPreview" class="loading-spinner">Generating PDF…</div>
      <embed *ngIf="previewUrl" [src]="previewUrl" type="application/pdf" class="pdf-viewer" />
    </div>
  </div>
</div>

<!-- Hidden file picker for the bulk upload; lives at the top level so the
     Bulk lines dialog and the header button can both reach the #fileInput ref. -->
<input #fileInput type="file" accept=".xlsx,.xls" (change)="onFileSelected($event); closeBulk()" class="d-none" />

<div [class.blurred]="showPreview">
  <div class="container-fluid py-3">
    <div class="content-info to-page">

      <!-- ── Sticky document header ─────────────────────────────────────── -->
      <div class="zf-doc-head">
        <div class="zf-doc-left">
          <button type="button" [routerLink]="['/transfer-out']" class="zf-doc-back" aria-label="Back">
            <i class="bi bi-arrow-left"></i> Transfer Out
          </button>
          <div class="zf-doc-title-row">
            <h3 class="zf-doc-title">
              {{ viewOnly ? 'View Transfer Out' : (transferOutId ? 'Edit Transfer Out' : 'New Transfer Out') }}
            </h3>
            @if (transferOutNumber) {
            <span class="zf-doc-no">{{ transferOutNumber }}</span>
            }
            @if (viewOnly) {
            <span class="zf-status dot">Read only</span>
            } @else if (transferOutId) {
            <span class="zf-status info dot">Editing</span>
            } @else {
            <span class="zf-status warn dot">Draft</span>
            }
          </div>
        </div>

        @if (!viewOnly) {
        <div class="zf-doc-actions">
          <button type="button" class="btn btn-secondary" (click)="openBulk()" [disabled]="isUploading"
            title="Download the template, fill it, upload it back">
            <i class="bi bi-upload"></i> {{ isUploading ? 'Uploading…' : 'Bulk lines' }}
          </button>
          <span class="zf-vsep"></span>
          <button type="button" class="btn btn-secondary" (click)="saveAsDraft()" [disabled]="!transferItems.length">
            Save draft
          </button>
          <!-- Disabled until a destination and at least one line exist; the
               click handler re-checks and explains, the server refuses regardless. -->
          <button type="button" class="btn zf-btn-hero" (click)="submitTransferOut()"
            [disabled]="!targetLocationId || !transferItems.length"
            [title]="!targetLocationId ? 'Select the destination store first' : (!transferItems.length ? 'Add at least one item' : '')">
            {{ transferOutId ? 'Update transfer out' : 'Submit transfer out' }}
          </button>
        </div>
        }
      </div>

      <!-- ── Transfer header ────────────────────────────────────────────── -->
      <div class="zf-doc-section">
        <div class="zf-section-head collapsible" [class.closed]="!headerOpen" (click)="headerOpen = !headerOpen">
          <span class="zf-section-title">Transfer header</span>
          <div class="zf-sum-chips">
            <span class="zf-sum-chip"><span class="k">From</span>{{ getSourceLocationName() }}</span>
            <span class="zf-sum-chip" [class.muted]="!targetLocationName">
              <span class="k">To</span>{{ targetLocationName || 'Not selected' }}
            </span>
            <span class="zf-sum-chip" [class.muted]="!selectedReasonLabel">
              <span class="k">Reason</span>{{ selectedReasonLabel || 'Not selected' }}
            </span>
            @if (remarks) {
            <span class="zf-sum-chip" [title]="remarks"><span class="k">Remarks</span>{{ remarks }}</span>
            }
            @if (transferItems.length) {
            <span class="zf-sum-chip primary">{{ transferItems.length }} {{ transferItems.length === 1 ? 'line' : 'lines' }}</span>
            }
          </div>
          <span class="zf-section-toggle">
            {{ headerOpen ? 'Collapse header' : (viewOnly ? 'Show header' : 'Edit header') }}
            <i class="bi" [class.bi-chevron-up]="headerOpen" [class.bi-chevron-down]="!headerOpen"></i>
          </span>
        </div>

        @if (headerOpen) {
        <div class="zf-section-body zf-stack">
          <div class="zf-fgrid" style="--cols: repeat(4, minmax(0,1fr))">
            <!-- From Location — follows the store/warehouse switcher at the top -->
            <div class="zf-field">
              <span class="zf-flabel">From location</span>
              <div class="zf-static">
                <i class="bi bi-building"></i>
                {{ getSourceLocationName() }}
              </div>
            </div>

            <!-- To Location -->
            <div class="zf-field">
              <label class="zf-flabel" for="to-loc">To location <span class="req">*</span></label>
              <select id="to-loc" class="form-select" [(ngModel)]="targetLocationId"
                [ngModelOptions]="{ standalone: true }" [disabled]="viewOnly">
                <option [ngValue]="null">— Select —</option>
                @for (loc of filteredTargetLocations; track loc) {
                <option [ngValue]="loc.id">{{ loc.name }}</option>
                }
              </select>
            </div>

            <!-- Reason -->
            <div class="zf-field">
              <label class="zf-flabel" for="reason">Reason <span class="meta">· required for store sources</span></label>
              @if (viewOnly) {
              <div class="zf-static">{{ selectedReasonDescription }}</div>
              } @else {
              <select id="reason" class="form-select" [(ngModel)]="selectedReasonId"
                [ngModelOptions]="{ standalone: true }">
                <option [ngValue]="null">— Select —</option>
                @for (r of reasons; track r.id) {
                <option [ngValue]="r.id">{{ r.shortReason }}</option>
                }
              </select>
              }
            </div>

            <!-- Remarks -->
            <div class="zf-field">
              <label class="zf-flabel" for="remarks">Remarks</label>
              <input id="remarks" class="form-control" [(ngModel)]="remarks"
                placeholder="Optional notes for the receiving store" [readOnly]="viewOnly" />
            </div>
          </div>
        </div>
        }
      </div>

      <!-- ── Transfer lines ─────────────────────────────────────────────── -->
      <div class="zf-doc-section zf-lines-card">
        <div class="zf-lines-head">
          <h2>Transfer lines</h2>
          <span class="zf-count">{{ lineCountLabel }}</span>
          <span class="zf-units">{{ getTotalQuantity() | number:'1.0-3' }} units</span>
          <div class="zf-pills">
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'all'" (click)="lineFilter = 'all'">
              All<span class="n">{{ transferItems.length }}</span>
            </button>
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'over'" (click)="lineFilter = 'over'">
              Over SOH<span class="n">{{ overSohCount }}</span>
            </button>
            <button type="button" class="zf-pill" [class.on]="lineFilter === 'out'" (click)="lineFilter = 'out'">
              Out of stock<span class="n">{{ outOfStockCount }}</span>
            </button>
            @if (lineFilter !== 'all' || lineQuery) {
            <button type="button" class="zf-pill clear" (click)="clearLineFilters()">✕ Clear filters</button>
            }
          </div>

          <div class="zf-lines-tools">
            <label class="zf-search" style="width:150px">
              <i class="bi bi-search"></i>
              <input [(ngModel)]="lineQuery" [ngModelOptions]="{ standalone: true }" placeholder="Filter these lines" />
            </label>

            @if (!viewOnly) {
            <!-- SKU search -->
            <div class="zf-tool" style="width:150px">
              <span class="zf-tool-k mono">{{ selectedSearchType.toUpperCase() }}</span>
              <ng-select [items]="searchResults" bindLabel="sku" [typeahead]="productCodeSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
                (change)="onSearchResultSelected($event); populatePriceOptions(selectedProduct)"
                [loading]="isCodeSearching"
                [placeholder]="'Type ' + selectedSearchType.toUpperCase() + '…'"
                [virtualScroll]="true" [clearable]="true" [readonly]="viewOnly">
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.sku }}</span>
                    <span class="ss-row__secondary">
                      {{ item.longDescription }}
                      @if (item.plu) { · PLU {{ item.plu }} }
                      @if (item.ean) { · EAN {{ item.ean }} }
                      · <span [class.text-danger]="!(item.variants?.[0]?.soh)">SOH {{ (item.variants?.[0]?.soh ?? 0) | number : "1.0-3" }}</span>
                    </span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <!-- EAN search (identity barcode or any price generation's EAN) -->
            <div class="zf-tool" style="width:150px">
              <span class="zf-tool-k mono">EAN</span>
              <ng-select [items]="eanSearchResults" bindLabel="matchedEan" [typeahead]="productEanSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
                (change)="onSearchResultSelected($event); populatePriceOptions(selectedProduct)"
                [loading]="isEanSearching" placeholder="Type / scan EAN…"
                [virtualScroll]="true" [clearable]="true" [readonly]="viewOnly">
                <ng-template ng-label-tmp let-item="item">{{ item.matchedEan || item.ean || item.sku }}</ng-template>
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.matchedEan }}</span>
                    <span class="ss-row__secondary">
                      {{ item.sku }} · {{ item.longDescription }}
                      · <span [class.text-danger]="!(item.variants?.[0]?.soh)">SOH {{ (item.variants?.[0]?.soh ?? 0) | number : "1.0-3" }}</span>
                    </span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <!-- Description search -->
            <div class="zf-tool" style="width:230px">
              <span class="zf-tool-k"><i class="bi bi-plus-lg"></i></span>
              <ng-select [items]="products" bindLabel="longDescription" [typeahead]="productSearch$"
                [(ngModel)]="selectedProduct" [ngModelOptions]="{ standalone: true }"
                (change)="onProductSelected($event)" [loading]="isSearching"
                placeholder="Search &amp; add product" [virtualScroll]="true" [disabled]="viewOnly">
                <ng-template ng-label-tmp let-item="item">{{ item.longDescription || item.skuCode }}</ng-template>
                <ng-template ng-option-tmp let-item="item">
                  <div class="ss-row">
                    <span class="ss-row__primary">{{ item.longDescription || item.skuCode }}</span>
                    <span class="ss-row__secondary">
                      <span [class.text-danger]="!(item.variants?.[0]?.soh)">SOH {{ (item.variants?.[0]?.soh ?? 0) | number : "1.0-3" }}</span>
                    </span>
                  </div>
                </ng-template>
              </ng-select>
            </div>

            <button type="button" class="btn btn-secondary" (click)="removeSelectedItems()" [disabled]="!selectedCount"
              title="Delete the ticked lines">
              <i class="bi bi-trash3"></i> Delete selected ({{ selectedCount }})
            </button>
            <button type="button" class="btn btn-secondary" (click)="clearTransferLines()" [disabled]="!transferItems.length">
              Clear lines
            </button>
            }
          </div>
        </div>

        <!-- Add-line strip: appears once a product is picked -->
        @if (!viewOnly) {
        @if (selectedProduct) {
        <div class="zf-add-strip">
          <div class="zf-add-prod">
            <span class="k">Adding</span>
            <span class="n" [title]="selectedProduct.longDescription || selectedProduct.name || selectedProduct.sku">
              {{ selectedProduct.longDescription || selectedProduct.name || selectedProduct.sku }}
            </span>
            <span class="s">SKU {{ selectedProduct.sku }}</span>
          </div>
          <div class="zf-field w-90">
            <span class="zf-flabel">Qty <span class="req">*</span></span>
            <input #quantityInput type="number" class="form-control"
              appQtyInput [allowDecimal]="allowsDecimal(selectedProduct)"
              [(ngModel)]="selectedQuantity" [readonly]="viewOnly"
              [min]="minQty(selectedProduct)" (keydown.enter)="confirmAddToTable()" />
          </div>
          <div class="zf-field">
            <span class="zf-flabel">Cost price</span>
            <select class="form-select" [(ngModel)]="enteredCostPrice"
              (ngModelChange)="onCostPriceChange()"
              [ngModelOptions]="{ standalone: true }" (keydown.enter)="confirmAddToTable()">
              @for (p of allCostPrices; track p) { <option [value]="p">{{ p }}</option> }
            </select>
          </div>
          <div class="zf-field">
            <span class="zf-flabel">MRP</span>
            <select class="form-select" [(ngModel)]="selectedMRP"
              (ngModelChange)="onMrpChange()"
              [ngModelOptions]="{ standalone: true }" (keydown.enter)="confirmAddToTable()">
              @for (m of allMRPs; track m) { <option [value]="m">{{ m }}</option> }
            </select>
          </div>
          <div class="zf-field w-90">
            <span class="zf-flabel">Stock</span>
            <span class="co-soh to-soh-pill" [class.co-soh--out]="!selectedSoh"
              title="Stock on hand at the source warehouse">
              {{ selectedSoh ?? '—' }}
            </span>
          </div>
          <button type="button" class="btn btnprimary" (click)="confirmAddToTable()">
            <i class="bi bi-plus-lg"></i> Add line
          </button>
          <button type="button" class="btn zf-btn-ghost" (click)="clearSelectedProduct()" title="Cancel">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        }

        <div class="zf-scoped">
          <span class="k">Stock</span>
          @if (allowNegativeStock) {
          <span>Negative warehouse stock is permitted — lines above SOH at <strong>{{ getSourceLocationName() }}</strong> are flagged, not blocked</span>
          } @else {
          <span>Quantity cannot exceed stock on hand at <strong>{{ getSourceLocationName() }}</strong></span>
          }
          <span class="hint">Rows locked left · scroll right for totals</span>
        </div>
        }

        <!-- SKU not-found warning -->
        @if (uploadNotFoundSkus && uploadNotFoundSkus.length > 0) {
        <div class="zf-alert danger" role="alert">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span><strong>{{ uploadNotFoundSkus.length }}</strong> SKU(s) not found: <em>{{ uploadNotFoundSkus.join(', ') }}</em></span>
        </div>
        }

        <!-- Rows whose MRP / COST_PRICE named no price generation of the SKU, or
             named more than one (MRP shared by two generations: CP needed). -->
        @if (uploadPriceNotFoundSkus.length > 0) {
        <div class="zf-alert warn">
          <i class="bi bi-exclamation-triangle"></i>
          <span><strong>{{ uploadPriceNotFoundSkus.length }}</strong> line(s) skipped — no such price:
            <em>{{ uploadPriceNotFoundSkus.join(', ') }}</em>. Check MRP / COST_PRICE against the SKU's current prices.</span>
        </div>
        }
        @if (uploadAmbiguousPriceSkus.length > 0) {
        <div class="zf-alert warn">
          <i class="bi bi-exclamation-triangle"></i>
          <span><strong>{{ uploadAmbiguousPriceSkus.length }}</strong> line(s) skipped — more than one price matches:
            <em>{{ uploadAmbiguousPriceSkus.join(', ') }}</em>. Fill COST_PRICE for these rows.</span>
        </div>
        }

        <!-- Counted SKUs whose sheet row carried a decimal. Reported rather than
             rounded: 2.5 on a PCS line could as easily have meant 25. -->
        @if (uploadFractionalSkus.length > 0) {
        <div class="zf-alert warn">
          <i class="bi bi-exclamation-triangle"></i>
          <span><strong>{{ uploadFractionalSkus.length }}</strong> line(s) skipped for decimal quantity:
            <em>{{ uploadFractionalSkus.join(', ') }}</em> — these items are counted in whole units.</span>
        </div>
        }

        <!-- Bulk SKUs skipped: a bulk sack must be broken into child SKUs before it
             can be transferred, so the upload drops those rows. -->
        @if (uploadBulkSkus && uploadBulkSkus.length > 0) {
        <div class="zf-alert danger" role="alert">
          <i class="bi bi-box-seam"></i>
          <span><strong>{{ uploadBulkSkus.length }}</strong> bulk SKU(s) skipped: <em>{{ uploadBulkSkus.join(', ') }}</em> — break them into child SKUs first.</span>
        </div>
        }

        @if (transferItems.length) {
        <div class="zf-sheet-wrap">
          <table class="zf-sheet" aria-label="Transfer out items">
            <thead>
              <tr>
                @if (!viewOnly) {
                <th class="st ctr" style="--l:0; width:36px">
                  <input type="checkbox" class="form-check-input m-0" [checked]="allItemsSelected"
                    (change)="toggleSelectAll($any($event.target).checked)" aria-label="Select all items" />
                </th>
                }
                <th class="st" [style.--l]="viewOnly ? '0' : '36px'" style="width:42px">#</th>
                <th class="st" [style.--l]="viewOnly ? '42px' : '78px'" style="width:96px">SKU</th>
                <th class="st st-end" [style.--l]="viewOnly ? '138px' : '174px'">Description</th>
                <th class="num" style="width:84px">Qty</th>
                <th class="num" style="width:96px">Cost</th>
                <th class="num" style="width:96px">Sell</th>
                <th class="num" style="width:96px">MRP</th>
                <th class="num" style="width:112px">Total</th>
                <th class="ctr" style="width:88px">SOH</th>
                @if (!viewOnly) {
                <th class="ctr" style="width:52px">Action</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of visibleItems; track row.item) {
              <tr [class.warn]="row.item.soh > 0 && row.item.quantity > row.item.soh">
                @if (!viewOnly) {
                <td class="st ctr" style="--l:0">
                  <input type="checkbox" class="form-check-input m-0" [checked]="isItemSelected(row.item)"
                    (change)="toggleItemSelection(row.item)" [attr.aria-label]="'Select ' + row.item.sku" />
                </td>
                }
                <td class="st zf-idx" [style.--l]="viewOnly ? '0' : '36px'">{{ row.item.srNo }}</td>
                <td class="st" [style.--l]="viewOnly ? '42px' : '78px'">
                  <span class="zf-sku">{{ row.item.sku }}</span>
                </td>
                <td class="st st-end zf-name" [style.--l]="viewOnly ? '138px' : '174px'">
                  <span class="n" [title]="row.item.description">{{ row.item.description }}</span>
                  <span class="s">
                    @switch (lineState(row.item)) {
                    @case ('over') { OVER SOH }
                    @case ('out') { OUT OF STOCK }
                    @default { {{ row.item.uom || 'IN STOCK' }} }
                    }
                  </span>
                </td>
                <td class="num">
                  <input type="number" class="zf-cell-in" [id]="'to-qty-' + row.index" [(ngModel)]="row.item.quantity"
                    appQtyInput [allowDecimal]="allowsDecimal(row.item)"
                    (ngModelChange)="updateItemQuantity(row.index)" [min]="minQty(row.item)" [disabled]="viewOnly"
                    [attr.aria-label]="'Qty for ' + row.item.sku" />
                </td>
                <td class="num">{{ row.item.costPrice | number:'1.2-2' }}</td>
                <td class="num ink3">{{ row.item.sellingPrice | number:'1.2-2' }}</td>
                <td class="num ink3">{{ row.item.mrp | number:'1.2-2' }}</td>
                <td class="num strong">{{ (row.item.netCost || 0) | number:'1.2-2' }}</td>
                <td class="ctr">
                  <span class="co-soh" [class.co-soh--out]="row.item.soh === 0">
                    {{ row.item.soh | number : "1.0-3" }}
                  </span>
                </td>
                @if (!viewOnly) {
                <td class="ctr">
                  <button type="button" class="zf-del" title="Delete line" (click)="removeItem(row.index)">
                    <i class="bi bi-trash3"></i>
                  </button>
                </td>
                }
              </tr>
              }
              @if (!visibleItems.length) {
              <tr>
                <td [attr.colspan]="viewOnly ? 9 : 11" class="zf-sheet-empty">
                  <div>
                    <span class="t">No lines match this filter</span>
                    <span class="s">Clear the filters to see all {{ transferItems.length }} lines.</span>
                  </div>
                </td>
              </tr>
              }
            </tbody>
            <tfoot>
              <tr>
                <td class="st" [attr.colspan]="viewOnly ? 3 : 4" style="--l:0"><span class="k">TOTAL</span>{{ lineCountLabel }}</td>
                <td class="num">{{ getTotalQuantity() | number:'1.0-3' }}</td>
                <td></td>
                <td></td>
                <td class="num dim">Grand total</td>
                <td class="num strong">₹ {{ grandTotal | number:'1.2-2' }}</td>
                <td></td>
                @if (!viewOnly) {
                <td></td>
                }
              </tr>
            </tfoot>
          </table>
        </div>
        } @else {
        <div class="zf-sheet-blank">
          <i class="bi bi-inbox"></i>
          <span class="t">No lines on this transfer yet</span>
          <span class="s">
            @if (viewOnly) { This transfer has no lines. }
            @else { Search a SKU above, or upload the filled template to add lines in bulk. }
          </span>
        </div>
        }
      </div>

      <!-- ── Transfer value strip ───────────────────────────────────────── -->
      <div class="zf-order-strip">
        <div class="zf-strip-info">
          <div class="zf-strip-line">
            <span class="dot" [class.ok]="transferItems.length"></span>
            {{ lineCountLabel }} · {{ getTotalQuantity() | number:'1.0-3' }} units
          </div>
          <div class="zf-strip-sub">
            @if (!viewOnly) {
            <button type="button" class="zf-link" (click)="openBulk()" [disabled]="isUploading">
              Upload lines from template
            </button>
            <span class="sep">·</span>
            }
            @if (!transferItems.length) {
            <span>Add at least one product to save a draft or submit.</span>
            } @else if (overSohCount) {
            <span>{{ overSohCount }} line{{ overSohCount === 1 ? '' : 's' }} exceed stock on hand at {{ getSourceLocationName() }}.</span>
            } @else {
            <span>Stock leaves {{ getSourceLocationName() }} only once the transfer is submitted · totals stay pinned to the grid</span>
            }
          </div>
        </div>
        <div class="zf-strip-kpis">
          <div class="zf-kpi">
            <span class="k">Lines</span>
            <span class="v">{{ transferItems.length }}</span>
          </div>
          <div class="zf-kpi">
            <span class="k">Units</span>
            <span class="v">{{ getTotalQuantity() | number:'1.0-3' }}</span>
          </div>
          @if (overSohCount) {
          <div class="zf-kpi">
            <span class="k">Over SOH</span>
            <span class="v text-danger">{{ overSohCount }}</span>
          </div>
          }
          <div class="zf-kpi dark">
            <span class="k">Transfer value · at cost</span>
            <span class="v">₹ {{ grandTotal | number:'1.2-2' }}</span>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- ── Bulk lines dialog ──────────────────────────────────────────────── -->
  @if (bulkOpen && !viewOnly) {
  <div class="zf-overlay" (click)="closeBulk()">
    <div class="zf-dialog w-600" (click)="$event.stopPropagation()">
      <div class="zf-dialog-head">
        <h3>Bulk transfer lines</h3>
        <span class="zf-dialog-sub">Download the format, fill it, upload it back</span>
        <button type="button" class="zf-x" aria-label="Close" (click)="closeBulk()">✕</button>
      </div>

      <div class="zf-dialog-body">
        <div class="zf-file-card">
          <div class="ic"><i class="bi bi-download"></i></div>
          <div class="min-w-0">
            <div class="t">Transfer line template</div>
            <div class="s">XLSX · SKU, quantity, MRP, cost price</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="downloadTemplate()">Download</button>
        </div>

        <div class="zf-dropzone">
          <i class="bi bi-upload"></i>
          <div>
            <div class="t">Upload the filled sheet</div>
            <div class="s">XLSX or XLS · one file at a time · lines are added as soon as the file is read</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" (click)="fileInput.click()" [disabled]="isUploading">
            {{ isUploading ? 'Uploading…' : 'Browse files' }}
          </button>
        </div>
      </div>

      <div class="zf-dialog-foot">
        <span class="note">Existing lines are kept; uploaded rows are appended. Rows whose SKU or price cannot be matched are reported above the grid.</span>
        <button type="button" class="btn btn-secondary" (click)="closeBulk()">Close</button>
      </div>
    </div>
  </div>
  }
</div>
